import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  getWeb3AuthEndpointAsync,
  refreshWeb3AuthEncryptionKeyAsync,
  selectWeb3AuthEndpoint,
  selectWeb3AuthEndpointActionLoading,
  selectWeb3AuthEndpointLoading,
  selectWeb3AuthEndpointUpdateLoading,
  updateWeb3AuthEndpointAsync,
} from "../../../store/web3-auth/web3AuthEndpointSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import { Web3EndpointUpdateSchema } from "../../../utils/validations";
import { useEffect, useState } from "react";
import {
  getWeb3NetworksAsync,
  selectWeb3Networks,
  selectWeb3NetworksLoading,
} from "../../../store/web3-auth/web3PublicSlice";
import UserDataCard from "./UserDataCard";
import { getWeb3AuthEndpointsAsync } from "../../../store/web3-auth/web3AuthEndpointsSlice";
import RefreshIcon from "./../../../assets/icons/arrow-path.svg?react";
import QuestionModal from "../../modals/QuestionModal";
import {
  Button,
  Typography,
  Card,
  Input,
  Checkbox,
  SecureCopy,
  MultiSelect,
} from "djuno-design";

const Web3AuthEndpointSettingTab = () => {
  const endpoint = useAppSelector(selectWeb3AuthEndpoint);
  const endpointLoading = useAppSelector(selectWeb3AuthEndpointLoading);

  const web3Networks = useAppSelector(selectWeb3Networks);
  const web3NetworksLoading = useAppSelector(selectWeb3NetworksLoading);

  const updateLoading = useAppSelector(selectWeb3AuthEndpointUpdateLoading);

  const dispatch = useAppDispatch();

  const [selectedNetworksOptions, setSelectedNetworksOptions] = useState<
    Array<string>
  >([]);

  const [updateEncryptionKey, setUpdateEncryptionKey] = useState(false);
  const actionLoading = useAppSelector(selectWeb3AuthEndpointActionLoading);

  const {
    setValue,
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(Web3EndpointUpdateSchema),
    mode: "all",
  });

  const handleUpdateApp = (data: any) => {
    // console.log(data);
    if (endpoint)
      dispatch(
        updateWeb3AuthEndpointAsync({
          id: endpoint.Id,
          data: {
            ...data,
            Networks: selectedNetworksOptions,
          },
        })
      ).then((action) => {
        if (action.type === "web3-auth/endpoint/update/fulfilled") {
          dispatch(getWeb3AuthEndpointAsync(endpoint.Id.toString())).then(
            () => {
              dispatch(getWeb3AuthEndpointsAsync());
            }
          );
        }
      });
  };

  useEffect(() => {
    if (endpoint) {
      setValue("Name", endpoint.Name);
      setValue("TokenLifeTime", endpoint.TokenLifeTime);
      // setValue("JWTEncryptionKey", endpoint.EncyptionKey);
      setValue("EnabledAvatar", endpoint.EnabledAvatar);
      setSelectedNetworksOptions([
        ...endpoint.NetworkItems.map((no) => no.Id.toString()),
      ]);
    }
  }, [endpoint, setValue]);

  useEffect(() => {
    if (web3Networks.length === 0) {
      dispatch(getWeb3NetworksAsync());
    }
  }, [dispatch, web3Networks.length]);

  return (
    <div className="w-full flex flex-col gap-8">
      <Card title="App setting">
        <div className="w-full md:w-3/4 lg:w-full xl:w-1/2">
          <form onSubmit={handleSubmit(handleUpdateApp)}>
            <div className="flex flex-col w-full  gap-4">
              <div className="flex flex-col md:flex-row md:items-center gap-1">
                <div className="w-36">
                  <Typography.Text className="!text-sm !whitespace-nowrap">
                    App Name:
                  </Typography.Text>
                </div>
                <div className="flex-1">
                  <Input {...register("Name")} error={errors.Name?.message} />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-1">
                <div className="w-36">
                  <Typography.Text className="!text-sm">
                    JWT life time:{" "}
                  </Typography.Text>
                </div>
                <div className="flex-1">
                  <Input
                    {...register("TokenLifeTime")}
                    error={errors.TokenLifeTime?.message}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-1">
                <div className="w-36">
                  <Typography.Text className="!text-sm">
                    JWT Encryption key:{" "}
                  </Typography.Text>
                </div>
                <div className="flex flex-1 items-center">
                  <SecureCopy
                    type="hide"
                    text={endpoint?.EncyptionKey || ""}
                    // className="!flex-1"
                    textClassName="!text-sm"
                    uiSize="medium"
                  />
                  <Button
                    uiType="icon"
                    uiSize="small"
                    onClick={(e) => {
                      e.preventDefault();
                      setUpdateEncryptionKey(true);
                    }}
                    tooltip={{
                      content: "Refresh Encryption key",
                      className: "!text-xs",
                    }}
                    className="ml-1 !px-2"
                  >
                    <RefreshIcon className="w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-1">
                <Controller
                  control={control}
                  name="EnabledAvatar"
                  render={({ field: { value, onChange } }) => (
                    <Checkbox
                      id="EnabledAvatar"
                      label="enable avatar"
                      value={value}
                      onChange={onChange}
                      error={errors.EnabledAvatar?.message}
                    />
                  )}
                />
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-1">
                <div className="w-36">
                  <Typography.Text className="!text-sm">Chains</Typography.Text>
                </div>
                <div className="flex-1">
                  <MultiSelect
                    label={
                      <Typography.Text className="!text-xs" uiType="secondary">
                        allow user to sign on multiple network and wallets
                      </Typography.Text>
                    }
                    options={web3Networks.map((n) => ({
                      label: n.NetworkName,
                      value: n.Id.toString(),
                    }))}
                    values={selectedNetworksOptions}
                    onChange={setSelectedNetworksOptions}
                    clearable
                    loading={web3NetworksLoading}
                    // emptyString="Select chains"
                    error={
                      selectedNetworksOptions.length === 0
                        ? "Select atleast one network!"
                        : undefined
                    }
                  />
                </div>
              </div>
              <div className="flex gap-1 items-center justify-end md:justify-start">
                <div className="w-36"></div>
                <Button
                  type="submit"
                  disabled={
                    endpointLoading || selectedNetworksOptions.length === 0
                  }
                  loading={updateLoading}
                  uiType="primary"
                >
                  Save changes
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Card>
      <UserDataCard />
      <QuestionModal
        isOpen={updateEncryptionKey}
        onClose={() => setUpdateEncryptionKey(false)}
        title="Refresh Encryption key"
        description="Are you sure you want to refresh the token?"
        onConfirm={() => {
          endpoint &&
            dispatch(
              refreshWeb3AuthEncryptionKeyAsync({ id: endpoint.Id })
            ).then((action) => {
              if (
                action.type ===
                "web3-auth/endpoint/encryption-key/update/fulfilled"
              ) {
                dispatch(getWeb3AuthEndpointAsync(endpoint.Id.toString()));
              }
              setUpdateEncryptionKey(false);
            });
        }}
        loading={actionLoading}
      />
    </div>
  );
};

export default Web3AuthEndpointSettingTab;
